/**
 * WMO Weather Code lookup — maps numeric codes from Open-Meteo
 * to human-readable labels and emoji.
 * Reference: https://open-meteo.com/en/docs#weathervariables
 */

interface WeatherDescription {
  label: string;
  emoji: string;
  /** Whether this weather suggests prioritising indoor places */
  prefersIndoor: boolean;
}

const WMO_CODES: Record<number, WeatherDescription> = {
  0:  { label: 'Clear sky',          emoji: '☀️', prefersIndoor: false },
  1:  { label: 'Mainly clear',       emoji: '🌤️', prefersIndoor: false },
  2:  { label: 'Partly cloudy',      emoji: '⛅',  prefersIndoor: false },
  3:  { label: 'Overcast',           emoji: '☁️', prefersIndoor: false },
  45: { label: 'Foggy',              emoji: '🌫️', prefersIndoor: false },
  48: { label: 'Icy fog',            emoji: '🌫️', prefersIndoor: true },
  51: { label: 'Light drizzle',      emoji: '🌦️', prefersIndoor: false },
  53: { label: 'Drizzle',            emoji: '🌦️', prefersIndoor: true },
  55: { label: 'Heavy drizzle',      emoji: '🌧️', prefersIndoor: true },
  56: { label: 'Freezing drizzle',   emoji: '🌧️', prefersIndoor: true },
  57: { label: 'Heavy freezing drizzle', emoji: '🌧️', prefersIndoor: true },
  61: { label: 'Light rain',         emoji: '🌦️', prefersIndoor: false },
  63: { label: 'Rain',               emoji: '🌧️', prefersIndoor: true },
  65: { label: 'Heavy rain',         emoji: '🌧️', prefersIndoor: true },
  66: { label: 'Freezing rain',      emoji: '🧊', prefersIndoor: true },
  67: { label: 'Heavy freezing rain', emoji: '🧊', prefersIndoor: true },
  71: { label: 'Light snow',         emoji: '🌨️', prefersIndoor: false },
  73: { label: 'Snow',               emoji: '❄️', prefersIndoor: true },
  75: { label: 'Heavy snow',         emoji: '❄️', prefersIndoor: true },
  77: { label: 'Snow grains',        emoji: '❄️', prefersIndoor: true },
  80: { label: 'Light showers',      emoji: '🌦️', prefersIndoor: false },
  81: { label: 'Showers',            emoji: '🌧️', prefersIndoor: true },
  82: { label: 'Heavy showers',      emoji: '🌧️', prefersIndoor: true },
  85: { label: 'Light snow showers', emoji: '🌨️', prefersIndoor: false },
  86: { label: 'Heavy snow showers', emoji: '❄️', prefersIndoor: true },
  95: { label: 'Thunderstorm',       emoji: '⛈️', prefersIndoor: true },
  96: { label: 'Thunderstorm with hail', emoji: '⛈️', prefersIndoor: true },
  99: { label: 'Severe thunderstorm', emoji: '🌪️', prefersIndoor: true },
};

const FALLBACK: WeatherDescription = { label: 'Unknown', emoji: '🌡️', prefersIndoor: false };

export function describeWeatherCode(code: number): WeatherDescription {
  return WMO_CODES[code] ?? FALLBACK;
}
